import { useState, useCallback, useMemo } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { NoticeBox } from "@/components/NoticeBox";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { loadPdfDocument } from "./utils";
import { readPageContent } from "./pdfPageContent";
import { pageContentToText } from "./pdfPlainText";
import { stemOfPdfName } from "./pdfMetadata";
import { pageCounterText } from "./extractPdfCounter";
import { getPdfPageCount, parseMultiRange, extractPdfPages } from "./operations";
import { usePdfPageThumbnails, PdfThumbnailLoading, PdfPageGrid } from "@/components/PdfPageThumbnails";
import { Check, Copy } from "lucide-react";

const TOOL_INFO_CONTENT = {
  what: `Pulls specific pages out of a PDF as a new, smaller PDF, or extracts the raw selectable text content as a plain text file. This is the tool for when you only need a portion of a larger document — pulling three relevant pages out of a 60-page report to send someone, or getting the plain text out of a document to paste into another program — rather than sharing or processing the whole file.\n\nIf you need several separate output files from ranges of pages (rather than one file with a handful of pages picked out), or want to split a document into equal-sized chunks, Split PDF is the more direct tool for that — Extract is built around a single deliverable: one set of pages, or the text.`,
  how: `Upload your PDF, choose Pages or Text. For page extraction, click any page thumbnail to select or deselect it (or type a range/list directly if you'd rather not click through many pages) — selected pages are pulled out into one new PDF, in page order when you click thumbnails, or in the order you typed when you use a list such as 5,1,3. For text extraction, the selectable text of the pages you choose (all of them by default) is shown, ready to copy, and can be downloaded as a plain text file, with columns in reading order.`,
  tip: `Text extraction reads columns in order, keeps tables as tab-separated cells and rebuilds paragraphs. It only works on PDFs with real, selectable text — a scanned document has no underlying text layer to extract, so the result will be empty for those. Run it through OCR PDF first if you're starting from a scan, which adds a real, searchable text layer that this tool can then pull from.`,
};

type Mode = "pages" | "text";

export default function ExtractPdf() {
  const { gateShare } = useShareGate();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>("pages");
  const [pageCount, setPageCount] = useState(0);
  const [pages, setPages] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Blob | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: "error" | "scan"; message: string } | null>(null);
  const [usedPages, setUsedPages] = useState<number[]>([]);
  const { thumbnails, loading: loadingThumbs } = usePdfPageThumbnails(file);

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null); setText(null); setNotice(null);
    const count = await getPdfPageCount(f);
    setPageCount(count);
    setPages("1");
    setSelected(new Set([1]));
  }, []);

  const togglePage = (n: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      // Keeps the text field in sync so either input method (clicking
      // thumbnails or typing a range) reflects the same real selection
      // state -- switching between them mid-task shouldn't silently
      // lose whichever one was used first.
      const sortedList = Array.from(next).sort((a, b) => a - b).join(",");
      setPages(sortedList);
      return next;
    });
    setResult(null); setText(null); setNotice(null);
  };

  const selectedSorted = useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected]);

  const setPageList = (list: number[]) => {
    setSelected(new Set(list));
    setPages(list.join(","));
    setResult(null); setText(null); setNotice(null);
  };
  const allPages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);

  /** Page numbers (1-based) from the box. Pages mode needs some; Text mode treats an empty box as "all pages". */
  const pagesFromBox = (): number[] | null => {
    if (!pages.trim()) {
      if (mode === "text") return allPages;
      setNotice({ kind: "error", message: "Choose at least one page: click a thumbnail or type pages such as 1, 3-5." });
      return null;
    }
    const parsed = parseMultiRange(pages, pageCount);
    if ("error" in parsed) { setNotice({ kind: "error", message: parsed.error }); return null; }
    return parsed.pages.map(i => i + 1);
  };

  const extract = async () => {
    setNotice(null);
    const nums = pagesFromBox();
    if (!nums || !file) return;
    setProcessing(true);
    try {
      setUsedPages(nums);
      if (mode === "pages") {
        setResult(await extractPdfPages(file, nums.map(n => n - 1)));
        toast.success(`${nums.length} page${nums.length === 1 ? "" : "s"} extracted`);
      } else {
        const pdf = await loadPdfDocument(file);
        const parts: string[] = [];
        for (const n of nums) {
          const content = await readPageContent(await pdf.getPage(n));
          parts.push(`--- Page ${n} ---\n\n${pageContentToText(content)}`);
        }
        const body = parts.join("\n\n");
        const hasAny = parts.some(p => p.replace(/^--- Page \d+ ---\s*/, "").trim());
        if (!hasAny) {
          setNotice({ kind: "scan", message: "No selectable text on these pages: this looks like a scanned image. Run it through OCR PDF first, then extract the text." });
          return;
        }
        setText(body);
        setResult(new Blob([body], { type: "text/plain;charset=utf-8" }));
        toast.success("Text extracted");
      }
    } catch (err) {
      console.error("Extraction failed:", err);
      setNotice({ kind: "error", message: err instanceof Error && err.message ? err.message : "Extraction failed. Check the page numbers and try again." });
    } finally {
      setProcessing(false);
    }
  };

  const copyText = async () => {
    if (!text) return;
    await gateShare(async () => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } });
  };

  const rangeLabel = (nums: number[]) => (nums.length === 1 ? `${nums[0]}` : `${Math.min(...nums)}-${Math.max(...nums)}`);
  const stem = file ? stemOfPdfName(file.name) : "";

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept=".pdf" onFiles={handleFiles} label="Drop a PDF file" description="Extract specific pages or the text content of a PDF" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name} — {pageCount} page{pageCount === 1 ? "" : "s"}</p></div>

          <div className="flex gap-2">
            <Button size="sm" aria-pressed={mode === "pages"} variant={mode === "pages" ? "default" : "outline"} onClick={() => { setMode("pages"); setResult(null); setText(null); setNotice(null); }}>Extract Pages</Button>
            <Button size="sm" aria-pressed={mode === "text"} variant={mode === "text" ? "default" : "outline"} onClick={() => { setMode("text"); setResult(null); setText(null); setNotice(null); }}>Extract Text</Button>
          </div>

          {(
            <div className="space-y-3">
              {loadingThumbs ? (
                <PdfThumbnailLoading />
              ) : (
                <>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setPageList(allPages)}>Select all</Button>
                  <Button size="sm" variant="ghost" onClick={() => setPageList(allPages.filter(n => n % 2 === 1))}>Odd pages</Button>
                  <Button size="sm" variant="ghost" onClick={() => setPageList(allPages.filter(n => n % 2 === 0))}>Even pages</Button>
                  <Button size="sm" variant="ghost" onClick={() => setPageList([])}>Clear</Button>
                </div>
                <PdfPageGrid thumbnails={thumbnails} selected={selected} onToggle={togglePage} />
                </>
              )}
              <div>
                <Label htmlFor="ex-pages">{mode === "text" ? "Pages to read (leave empty for all pages)" : "Or type pages directly"} (e.g. &quot;1, 3-5, 7&quot;)</Label> 
                <Input
                  id="ex-pages"
                  value={pages}
                  onChange={e => {
                    setPages(e.target.value);
                    setResult(null); setText(null); setNotice(null);
                    if (!e.target.value.trim()) { setSelected(new Set()); return; }
                    const parsed = parseMultiRange(e.target.value, pageCount);
                    if (!("error" in parsed)) setSelected(new Set(parsed.pages.map(i => i + 1)));
                  }}
                />
              </div>
              {pageCounterText(mode, pages, selectedSorted.length, pageCount) && (
                <p className="text-xs text-muted-foreground" aria-live="polite">{pageCounterText(mode, pages, selectedSorted.length, pageCount)}</p>
              )}
            </div>
          )}

          {notice && (
            notice.kind === "error"
              ? <p role="alert" className="text-sm text-destructive">{notice.message}</p>
              : (
                <NoticeBox role="alert" className="text-sm">
                  {notice.message} {notice.kind === "scan" && <a className="underline" href="/tool/ocr-pdf">Open OCR PDF</a>}
                </NoticeBox>
              )
          )}

          <div className="flex gap-3">
            {processing ? <ProcessingState /> : result ? (
              <>
                <DownloadButton blob={result} fileName={mode === "pages" ? `${stem}-pages-${rangeLabel(usedPages)}.pdf` : `${stem}-text.txt`} label={mode === "pages" ? "Download PDF" : "Download Text File"}
                />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <><Button onClick={extract}>{mode === "pages" ? "Extract Pages" : "Extract Text"}</Button><Button variant="outline" onClick={() => setFile(null)}>Reset</Button></>
            )}
          </div>
          {result && mode === "pages" && <ResultPreview blob={result} mimeType="application/pdf" />}
          {result && mode === "text" && text !== null && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Extracted text ({text.length.toLocaleString()} characters)</p>
                <Button size="sm" variant="outline" onClick={copyText}><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
              </div>
              <textarea readOnly value={text} className="w-full h-64 rounded-md border bg-background p-3 text-sm font-mono" aria-label="Extracted text" />
            </div>
          )}
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
